// src/pages/Tickets/TicketList.jsx
// -----------------------------------------------------------------------------
// Lists all tickets and provides full CRUD capabilities using reusable components.
// Performs full CRUD (Read, Create, Update, Delete) locally or via ticketService.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ticketService, { mockSeverities, mockStatuses, mockEnvironments } from '../../api/ticketService';
import projectService from '../../api/projectService';
import ticketTypeService from '../../api/ticketTypeService';
import useTableData from '../../hooks/useTableData';
import useTicketFormOptions from '../../hooks/useTicketFormOptions';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import StatusBadge from '../../components/common/StatusBadge';

export default function TicketList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get status, severity, date, and dashboard special filters from URL
  const statusParam = searchParams.get('statusID') || searchParams.get('status');
  const severityParam = searchParams.get('severityID') || searchParams.get('severity');
  const dateParam = searchParams.get('date') || searchParams.get('createDate') || searchParams.get('createdDate');
  const filterParam = searchParams.get('filter');

  const [selectedStatusFilter, setSelectedStatusFilter] = useState(statusParam || '');
  const [selectedSeverityFilter, setSelectedSeverityFilter] = useState(severityParam || '');
  const [selectedDateFilter, setSelectedDateFilter] = useState(dateParam || '');
  const [activeSpecialFilter, setActiveSpecialFilter] = useState(filterParam || '');

  // Sync filters if URL search params change
  useEffect(() => {
    setSelectedStatusFilter(statusParam || '');
    setSelectedSeverityFilter(severityParam || '');
    setSelectedDateFilter(dateParam || '');
    setActiveSpecialFilter(filterParam || '');
  }, [statusParam, severityParam, dateParam, filterParam]);

  // Initialize CRUD Hook
  const {
    data,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(ticketService, {
    idKey: 'ticketID',
    itemName: 'Ticket',
    initialSort: { key: 'ticketID', direction: 'desc' },
  });

  // Local pagination states (to properly support custom filters)
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [localPageSize, setLocalPageSize] = useState(10);

  // Filter tickets locally
  const filteredData = useMemo(() => {
    let result = data;

    // Apply dashboard special filters
    if (activeSpecialFilter === 'client_replies') {
      result = result.filter((item) => item.clientOrgID && Number(item.statusID) !== 4);
    } else if (activeSpecialFilter === 'staff_replies') {
      result = result.filter((item) => [2, 3, 4].includes(Number(item.statusID)));
    } else if (activeSpecialFilter === 'without_reply') {
      result = result.filter((item) => Number(item.statusID) === 1);
    } else if (activeSpecialFilter === 'resolved') {
      result = result.filter((item) => [3, 4].includes(Number(item.statusID)) || String(item.statusName || '').toUpperCase() === 'RESOLVED' || String(item.statusName || '').toUpperCase() === 'CLOSED');
    }

    if (selectedStatusFilter) {
      result = result.filter((item) => Number(item.statusID) === Number(selectedStatusFilter));
    }
    if (selectedSeverityFilter) {
      result = result.filter((item) => Number(item.severityID) === Number(selectedSeverityFilter));
    }
    if (selectedDateFilter) {
      result = result.filter((item) => {
        if (!item.createDate) return false;
        try {
          const cDate = new Date(item.createDate);
          if (isNaN(cDate.getTime())) return false;

          const y = cDate.getFullYear();
          const m = String(cDate.getMonth() + 1).padStart(2, '0');
          const d = String(cDate.getDate()).padStart(2, '0');
          const localYMD = `${y}-${m}-${d}`;
          const isoYMD = cDate.toISOString().slice(0, 10);

          let targetYMD = String(selectedDateFilter).trim();
          if (targetYMD.includes('T')) {
            targetYMD = targetYMD.slice(0, 10);
          } else if (targetYMD.length > 10) {
            const targetDateObj = new Date(targetYMD);
            if (!isNaN(targetDateObj.getTime())) {
              targetYMD = targetDateObj.toISOString().slice(0, 10);
            }
          }
          return localYMD === targetYMD || isoYMD === targetYMD;
        } catch (e) {
          return false;
        }
      });
    }
    return result;
  }, [data, activeSpecialFilter, selectedStatusFilter, selectedSeverityFilter, selectedDateFilter]);

  // Slice for current page
  const paginatedTickets = useMemo(() => {
    const startIndex = (localCurrentPage - 1) * localPageSize;
    return filteredData.slice(startIndex, startIndex + localPageSize);
  }, [filteredData, localCurrentPage, localPageSize]);

  const localTotalPages = Math.ceil(filteredData.length / localPageSize) || 1;
  const localTotalRecords = filteredData.length;

  // Reset pagination if search or filters change
  useEffect(() => {
    setLocalCurrentPage(1);
  }, [searchTerm, activeSpecialFilter, selectedStatusFilter, selectedSeverityFilter, selectedDateFilter]);

  // Use the dedicated cascading forms hook
  const {
    projects,
    phases,
    ticketTypes,
    allSubTypes,
    environments,
    severities,
    statuses,
    filteredPhases,
    filteredSubTypes,
    filteredEnvironments,
    filteredSeverities,
    filteredStatuses,
    currentFormValues,
    handleFieldChange,
  } = useTicketFormOptions(selectedRecord);

  // Open create modal if URL search params have openCreate=true
  useEffect(() => {
    if (searchParams.get('openCreate') === 'true') {
      openCreateModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Schema configuration for Ticket_TBL
  const ticketFields = useMemo(() => {
    const isEditMode = formMode === 'edit';
    const list = [];

    // Only show Ticket # in edit mode
    if (isEditMode) {
      list.push({
        name: 'ticketNumber',
        label: 'Ticket #',
        type: 'varchar',
        readOnly: true,
        placeholder: 'TCK-XXX (System Generated)',
      });
    }

    list.push(
      {
        name: 'projectID',
        label: 'Project',
        type: 'select',
        required: true,
        options: projects.map((p) => ({ label: `${p.projectCode} — ${p.projectName}`, value: p.projectID })),
      },
      {
        name: 'phaseID',
        label: 'Phase',
        type: 'select',
        required: true,
        options: filteredPhases.map((ph) => ({ label: ph.phaseName, value: ph.phaseID })),
      },
      {
        name: 'ticketTypeID',
        label: 'Ticket Type',
        type: 'select',
        required: true,
        options: ticketTypes.map((t) => ({ label: t.typeName, value: t.ticketTypeID })),
      },
      {
        name: 'ticketSubTypeID',
        label: 'Sub-Type',
        type: 'select',
        options: filteredSubTypes.map((sub) => ({ label: sub.subTypeName, value: sub.subTypeID })),
      },
      {
        name: 'title',
        label: 'Title',
        type: 'varchar',
        required: true,
        gridSpan: 'full',
        maxLength: 200,
        placeholder: 'Summarize the issue briefly...',
      },
      {
        name: 'description',
        label: 'Description',
        type: 'textarea',
        required: true,
        maxLength: 1000,
        placeholder: 'Describe the bug, task, or user story...',
      },
      {
        name: 'detailedDescription',
        label: 'Detailed Description',
        type: 'textarea',
        placeholder: 'Add steps to reproduce, actual vs expected results...',
      },
      {
        name: 'severityID',
        label: 'Severity',
        type: 'select',
        required: true,
        options: filteredSeverities.map((s) => ({ label: s.severityName, value: s.severityID })),
      }
    );

    // Only allow editing status in edit mode (defaults to Open on creation)
    if (isEditMode) {
      list.push({
        name: 'statusID',
        label: 'Status',
        type: 'select',
        required: true,
        options: filteredStatuses.map((st) => ({ label: st.statusName, value: st.statusID })),
      });
    }

    list.push({
      name: 'environmentID',
      label: 'Environment',
      type: 'select',
      options: filteredEnvironments.map((env) => ({ label: env.environmentName, value: env.environmentID })),
    });

    return list;
  }, [projects, filteredPhases, ticketTypes, filteredSubTypes, filteredSeverities, filteredStatuses, filteredEnvironments, formMode]);

  const columns = [
    { key: 'ticketID', label: 'ID', sortable: true, width: '70px' },
    { key: 'ticketNumber', label: 'Ticket ', sortable: true, width: '100px' },
    { key: 'title', label: 'Title', sortable: true },
    {
      key: 'projectID',
      label: 'Project',
      sortable: true,
      render: (row) => {
        const proj = projects.find((p) => Number(p.projectID) === Number(row.projectID));
        return proj ? proj.projectName : '-';
      },
    },
    {
      key: 'ticketTypeID',
      label: 'Type',
      sortable: true,
      render: (row) => {
        const type = ticketTypes.find((t) => Number(t.ticketTypeID) === Number(row.ticketTypeID));
        return type ? type.typeName : '-';
      },
    },
    {
      key: 'severityID',
      label: 'Severity',
      sortable: true,
      render: (row) => {
        const sev = severities.find((s) => Number(s.severityID) === Number(row.severityID))
          || mockSeverities.find((s) => Number(s.severityID) === Number(row.severityID));
        return sev ? <StatusBadge value={sev.severityName} /> : '-';
      },
    },
    {
      key: 'statusID',
      label: 'Status',
      sortable: true,
      render: (row) => {
        const st = statuses.find((s) => Number(s.statusID) === Number(row.statusID))
          || mockStatuses.find((s) => Number(s.statusID) === Number(row.statusID));
        return st ? <StatusBadge value={st.statusName} /> : '-';
      },
    },
    {
      key: 'createDate',
      label: 'Created Date',
      sortable: true,
      render: (row) => {
        if (!row.createDate) return '-';
        const d = new Date(row.createDate);
        return isNaN(d.getTime()) ? row.createDate : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      },
    },
  ];

  const renderHeaderActions = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      {/* Status Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <label htmlFor="statusFilterSelect" style={{ fontWeight: '500', fontSize: '13px', margin: 0, whiteSpace: 'nowrap' }}>
          Status:
        </label>
        <select
          id="statusFilterSelect"
          value={selectedStatusFilter}
          onChange={(e) => setSelectedStatusFilter(e.target.value)}
          style={{
            padding: '6px 12px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
            fontSize: '13px',
            backgroundColor: 'var(--color-bg)',
            color: 'var(--color-text)',
            minWidth: '120px'
          }}
        >
          <option value="">All Statuses</option>
          {(statuses.length > 0 ? statuses : mockStatuses).map((s) => (
            <option key={s.statusID} value={s.statusID}>
              {s.statusName}
            </option>
          ))}
        </select>
      </div>

      {/* Severity Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <label htmlFor="severityFilterSelect" style={{ fontWeight: '500', fontSize: '13px', margin: 0, whiteSpace: 'nowrap' }}>
          Severity:
        </label>
        <select
          id="severityFilterSelect"
          value={selectedSeverityFilter}
          onChange={(e) => setSelectedSeverityFilter(e.target.value)}
          style={{
            padding: '6px 12px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
            fontSize: '13px',
            backgroundColor: 'var(--color-bg)',
            color: 'var(--color-text)',
            minWidth: '120px'
          }}
        >
          <option value="">All Severities</option>
          {(severities.length > 0 ? severities : mockSeverities).map((s) => (
            <option key={s.severityID} value={s.severityID}>
              {s.severityName}
            </option>
          ))}
        </select>
      </div>

      {/* Date Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <label htmlFor="dateFilterInput" style={{ fontWeight: '500', fontSize: '13px', margin: 0, whiteSpace: 'nowrap' }}>
          Date:
        </label>
        <input
          type="date"
          id="dateFilterInput"
          value={selectedDateFilter}
          onChange={(e) => setSelectedDateFilter(e.target.value)}
          style={{
            padding: '5px 10px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
            fontSize: '13px',
            backgroundColor: 'var(--color-bg)',
            color: 'var(--color-text)',
          }}
        />
      </div>

      {(selectedStatusFilter || selectedSeverityFilter || selectedDateFilter || activeSpecialFilter) && (
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => {
            setSelectedStatusFilter('');
            setSelectedSeverityFilter('');
            setSelectedDateFilter('');
            setActiveSpecialFilter('');
          }}
          style={{ padding: '6px 12px', fontSize: '12px' }}
        >
          Clear All Filters
        </button>
      )}
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Tickets</h1>
          <div className="subtitle">
            Full CRUD tracking for bugs, tasks, and stories.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Ticket
        </button>
      </div>

      {/* Main Table list */}
      {activeSpecialFilter && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          padding: '10px 16px',
          borderRadius: '8px',
          color: '#c2410c',
          fontSize: '13px',
          fontWeight: '500'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '15px' }}>🔍</span>
            <span>
              Filtered from Dashboard: <strong>{
                activeSpecialFilter === 'client_replies' ? 'Client Replies' :
                activeSpecialFilter === 'staff_replies' ? 'Staff Replies (In Progress / QA / Handling)' :
                activeSpecialFilter === 'without_reply' ? 'Tickets Without Reply (New / Unhandled)' :
                activeSpecialFilter === 'resolved' ? 'Resolved / Closed Tickets' : activeSpecialFilter
              }</strong> ({filteredData.length} {filteredData.length === 1 ? 'ticket' : 'tickets'} found)
            </span>
          </div>
          <button
            onClick={() => {
              setActiveSpecialFilter('');
              navigate('/tickets');
            }}
            style={{
              background: '#ea580c',
              color: '#ffffff',
              border: 'none',
              padding: '5px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              boxShadow: '0 1px 3px rgba(234, 88, 12, 0.3)'
            }}
          >
            ✕ Show All Tickets
          </button>
        </div>
      )}

      {/* Date Filter Notification Banner */}
      {selectedDateFilter && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          padding: '10px 16px',
          borderRadius: '8px',
          color: '#1e40af',
          fontSize: '13px',
          fontWeight: '500'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '15px' }}>📅</span>
            <span>
              Showing all tickets recorded on <strong>{
                (() => {
                  try {
                    const d = new Date(selectedDateFilter);
                    return isNaN(d.getTime()) ? selectedDateFilter : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                  } catch (e) {
                    return selectedDateFilter;
                  }
                })()
              }</strong> ({filteredData.length} {filteredData.length === 1 ? 'ticket' : 'tickets'} found)
            </span>
          </div>
          <button
            onClick={() => {
              setSelectedDateFilter('');
              navigate('/tickets');
            }}
            style={{
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              padding: '5px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              boxShadow: '0 1px 3px rgba(37, 99, 235, 0.3)'
            }}
          >
            ✕ Show All Dates
          </button>
        </div>
      )}

      <TableList
        columns={columns}
        data={filteredData}
        paginatedData={paginatedTickets}
        loading={loading}
        idKey="ticketID"
        emptyMessage="No tickets found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        renderHeaderActions={renderHeaderActions}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={localCurrentPage}
        totalPages={localTotalPages}
        totalRecords={localTotalRecords}
        pageSize={localPageSize}
        onPageChange={setLocalCurrentPage}
        onPageSizeChange={setLocalPageSize}
        // Row Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
        renderActions={(row) => (
          <button
            className="btn-table-action btn-table-action-primary"
            onClick={() => navigate(`/tickets/${row.ticketID}`)}
          >
            View
          </button>
        )}
      />

      {/* Form Modal for Create & Edit */}
      {isFormOpen && (
        <TableForm
          fields={ticketFields}
          initialValues={currentFormValues}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          onFieldChange={handleFieldChange}
          itemName="Ticket"
        />
      )}

      {/* Confirmation Delete Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Ticket"
        itemLabel={selectedRecord ? selectedRecord.title : 'Ticket'}
        deleting={deleting}
      />
    </div>
  );
}